import type { WebContainer } from '@webcontainer/api';
import { hashFileContent } from '../utils/workbenchFileHash';

const OPFS_ROOT = 'wc-deps';

function supportsOpfs(): boolean {
  return typeof navigator !== 'undefined' && 'storage' in navigator && 'getDirectory' in navigator.storage;
}

export function getTemplateLockHash(packageLockJson: string): string {
  return hashFileContent(packageLockJson);
}

async function opfsFilePath(lockHash: string): Promise<FileSystemFileHandle | null> {
  if (!supportsOpfs()) return null;
  try {
    const root = await navigator.storage.getDirectory();
    const depsDir = await root.getDirectoryHandle(OPFS_ROOT, { create: true });
    return depsDir.getFileHandle(`${lockHash}.bin`, { create: true });
  } catch {
    return null;
  }
}

const MIN_DEPS_SNAPSHOT_BYTES = 1024;

/** Reject HTML/JSON error pages and empty blobs mistaken for wc binary snapshots. */
export function looksLikeWebContainerBinarySnapshot(buffer: ArrayBuffer | Uint8Array): boolean {
  if (buffer.byteLength < MIN_DEPS_SNAPSHOT_BYTES) return false;
  const bytes =
    buffer instanceof Uint8Array
      ? buffer
      : new Uint8Array(buffer, 0, Math.min(16, buffer.byteLength));
  if (bytes[0] === 0x3c) return false; // '<' HTML
  if (bytes[0] === 0x7b) return false; // '{' JSON
  return true;
}

export async function removeDepsCacheFromOpfs(lockHash: string): Promise<void> {
  if (!supportsOpfs()) return;
  try {
    const root = await navigator.storage.getDirectory();
    const depsDir = await root.getDirectoryHandle(OPFS_ROOT);
    await depsDir.removeEntry(`${lockHash}.bin`);
  } catch {
    /* already gone */
  }
}

export async function readDepsCacheFromOpfs(lockHash: string): Promise<ArrayBuffer | null> {
  if (!supportsOpfs()) return null;
  try {
    const root = await navigator.storage.getDirectory();
    const depsDir = await root.getDirectoryHandle(OPFS_ROOT);
    const handle = await depsDir.getFileHandle(`${lockHash}.bin`);
    const file = await handle.getFile();
    if (file.size === 0) return null;
    const buf = await file.arrayBuffer();
    if (!looksLikeWebContainerBinarySnapshot(buf)) {
      await removeDepsCacheFromOpfs(lockHash);
      return null;
    }
    return buf;
  } catch {
    return null;
  }
}

/** Accepts the `Uint8Array` from `wc.export` as is; copying it first doubled the archive in memory. */
export async function writeDepsCacheToOpfs(
  lockHash: string,
  data: ArrayBuffer | Uint8Array,
): Promise<void> {
  const handle = await opfsFilePath(lockHash);
  if (!handle) return;
  const writable = await handle.createWritable();
  // A `FileSystemWritableFileStream` holds an exclusive lock on the entry until it is closed or
  // aborted. Without this, a throwing `write()` (quota, eviction, contention) skipped `close()`
  // and leaked that lock for the rest of the page's life — every later write to the same
  // `<lockHash>.bin` then failed too, and the caller's outer catch swallowed it silently.
  try {
    await writable.write(data as FileSystemWriteChunkType);
    await writable.close();
  } catch (err) {
    try {
      await writable.abort();
    } catch {
      /* stream already errored out — the lock is released either way */
    }
    throw err;
  }
}

/** OPFS/binary mounts drop execute bits on `node_modules/.bin/*`, breaking `npm run dev`. */
export async function fixNodeModulesBinPermissions(wc: WebContainer): Promise<void> {
  try {
    const proc = await wc.spawn('chmod', ['-R', '755', 'node_modules/.bin']);
    await proc.exit;
  } catch {
    /* best-effort — callers can still use npx */
  }
}

export async function mountDepsFromBuffer(
  wc: WebContainer,
  buffer: ArrayBuffer,
): Promise<boolean> {
  if (!looksLikeWebContainerBinarySnapshot(buffer)) {
    return false;
  }
  try {
    // WebContainer requires the mount point directory to exist before mount().
    await wc.fs.mkdir('node_modules', { recursive: true });
    await wc.mount(buffer, { mountPoint: 'node_modules' });
    await fixNodeModulesBinPermissions(wc);
    return true;
  } catch {
    return false;
  }
}

export async function exportAndCacheNodeModules(
  wc: WebContainer,
  lockHash: string,
): Promise<void> {
  try {
    const exported: unknown = await wc.export('node_modules', { format: 'binary' });
    if (
      (exported instanceof ArrayBuffer || exported instanceof Uint8Array) &&
      exported.byteLength > 0
    ) {
      await writeDepsCacheToOpfs(lockHash, exported);
    }
  } catch {
    /* best-effort cache */
  }
}

/** Idle checks before giving up on caching `node_modules` for this session. */
const IDLE_EXPORT_MAX_ATTEMPTS = 20;
const IDLE_EXPORT_RETRY_MS = 15_000;

type IdleScheduler = {
  requestIdle: (cb: () => void) => () => void;
  wait: (ms: number, cb: () => void) => () => void;
};

const browserIdleScheduler: IdleScheduler = {
  requestIdle: (cb) => {
    const w = globalThis as typeof globalThis & {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
      cancelIdleCallback?: (id: number) => void;
    };
    if (typeof w.requestIdleCallback === 'function') {
      const id = w.requestIdleCallback(cb, { timeout: 10_000 });
      return () => w.cancelIdleCallback?.(id);
    }
    const id = setTimeout(cb, 2_000);
    return () => clearTimeout(id);
  },
  wait: (ms, cb) => {
    const id = setTimeout(cb, ms);
    return () => clearTimeout(id);
  },
};

/**
 * `wc.export('node_modules')` materialises the whole tree as one archive in memory. Doing that
 * right after `npm ci`, while the first build and preview also run, is the memory peak that
 * fails on low-memory devices (WC-1). This waits for an idle moment when `isBusy()` is false
 * (no stream, build or client tool) and gives up after `IDLE_EXPORT_MAX_ATTEMPTS` checks.
 * Returns a cancel function.
 */
export function scheduleIdleNodeModulesExport(
  wc: WebContainer,
  lockHash: string,
  opts: {
    isBusy: () => boolean;
    scheduler?: IdleScheduler;
    exportFn?: (wc: WebContainer, lockHash: string) => Promise<void>;
  },
): () => void {
  const scheduler = opts.scheduler ?? browserIdleScheduler;
  const exportFn = opts.exportFn ?? exportAndCacheNodeModules;
  let attempts = 0;
  let cancel: () => void = () => {};
  let cancelled = false;

  const tryExport = (): void => {
    if (cancelled) return;
    attempts += 1;
    if (opts.isBusy()) {
      if (attempts >= IDLE_EXPORT_MAX_ATTEMPTS) return;
      cancel = scheduler.wait(IDLE_EXPORT_RETRY_MS, () => {
        cancel = scheduler.requestIdle(tryExport);
      });
      return;
    }
    void exportFn(wc, lockHash);
  };

  cancel = scheduler.requestIdle(tryExport);
  return () => {
    cancelled = true;
    cancel();
  };
}

const PREBUILT_CACHE = 'wc-prebuilt-deps-v1';

export async function fetchPrebuiltNodeModules(
  lockHash: string,
  publicUrl: string,
): Promise<ArrayBuffer | null> {
  if (typeof caches !== 'undefined') {
    try {
      const cache = await caches.open(PREBUILT_CACHE);
      const cached = await cache.match(publicUrl);
      if (cached) {
        const buf = await cached.arrayBuffer();
        if (looksLikeWebContainerBinarySnapshot(buf)) {
          return buf;
        }
        await cache.delete(publicUrl);
      }
    } catch {
      /* ignore */
    }
  }

  try {
    const res = await fetch(publicUrl, { priority: 'low' } as RequestInit);
    if (!res.ok) return null;
    const contentType = res.headers.get('content-type') ?? '';
    if (contentType.includes('text/html')) return null;

    // The Cache API streams its copy of the body to disk; `new Response(buf.slice(0))` used to
    // hold a second full copy of the archive in memory while it was written.
    const cachePut =
      typeof caches !== 'undefined'
        ? caches
            .open(PREBUILT_CACHE)
            .then((cache) => cache.put(publicUrl, res.clone()))
            .then(() => true)
            .catch(() => false)
        : Promise.resolve(false);
    const buf = await res.arrayBuffer();
    const cached = await cachePut;

    if (!looksLikeWebContainerBinarySnapshot(buf)) {
      if (cached) {
        void caches
          .open(PREBUILT_CACHE)
          .then((cache) => cache.delete(publicUrl))
          .catch(() => undefined);
      }
      return null;
    }
    return buf;
  } catch {
    return null;
  }
}

export function getPrebuiltNodeModulesUrl(lockHash: string): string {
  const base = typeof import.meta !== 'undefined' && import.meta.env?.BASE_URL
    ? String(import.meta.env.BASE_URL)
    : '/';
  const normalizedBase = base.endsWith('/') ? base : `${base}/`;
  return `${normalizedBase}wc/node_modules-${lockHash}.bin`;
}
